import {
  useInfiniteQuery,
  useSuspenseInfiniteQuery,
} from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

import * as Comments from "./getQueryKey";

export function getQueryKey(orgId: string) {
  return [...Comments.getQueryKey(orgId), "recent"];
}

/**
 * Fetches the most recent components for the org
 */
export function useRecentComments() {
  const { commentService } = useDomainServices();
  const { currentOrganization } = useAuth();

  const orgId: string = currentOrganization?.org_id ?? "";

  return useInfiniteQuery({
    queryKey: getQueryKey(orgId),
    queryFn: async ({ pageParam, signal }) => {
      if (!orgId) {
        throw new Error("No organization selected");
      }

      return commentService.getRecentComments({
        orgId,
        pageToken: pageParam,
        abortSignal: signal,
      });
    },
    select: (data) => data.pages.flatMap((page) => page.items),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_token,
    enabled: currentOrganization !== null,
    staleTime: 60000, // 1 minute
  });
}

/**
 * Fetches the most recent components for the org
 */
export function useRecentCommentsSuspense() {
  const { commentService } = useDomainServices();
  const { currentOrganization } = useAuth();

  const orgId: string = currentOrganization?.org_id ?? "";

  return useSuspenseInfiniteQuery({
    queryKey: getQueryKey(orgId),
    queryFn: async ({ pageParam, signal }) => {
      if (!currentOrganization) {
        throw new Error("No organization selected");
      }

      return commentService.getRecentComments({
        orgId: currentOrganization.org_id,
        pageToken: pageParam,
        abortSignal: signal,
      });
    },
    select: (data) => data.pages.flatMap((page) => page.items),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_token,
    staleTime: 60000, // 1 minute
  });
}
